import { useCallback, useEffect, useState } from 'react';
import { ActionIcon, Anchor, Box, Button, CopyButton, Group, Loader, Modal, Text, TextInput, Tooltip, useMantineColorScheme } from '@mantine/core';
import { IconCircleCheck, IconRefresh, IconClockHour4, IconCopy, IconCheck } from '@tabler/icons-react';
import SettingsApi from '../../../api/SettingsApi';
import { webhookTestStep } from '../../GeneralComponent/webhookTestSteps';
import { platformGuidesFor, ADVANCED_WEBHOOK_DOC_URL } from '../../GeneralComponent/platformSpecificGuides';

interface VerifyWebhookModalProps {
  opened: boolean;
  onClose: () => void;
  // Encrypted manage_webhook id returned by insert_webhooks
  webhookId: string;
  // Backend processor key, e.g. 'youform' — drives the instructions, name and icon
  processor: string;
  // Generated capture URL the user must paste into the platform
  webhookUrl?: string;
  webhookName?: string;
}

// Renders the DB datetime in the viewer's locale, e.g. "July 13, 2026, 3:45 PM"
const formatVerifiedAt = (value: string) => {
  if (!value) return '';
  const iso = value.replace(' ', 'T');
  const d = new Date(iso);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

// Shown after saving a webhook for a platform that can fire a test event.
// Mirrors the onboarding guided-setup verification card.
export function VerifyWebhookModal({
  opened,
  onClose,
  webhookId,
  processor,
  webhookUrl,
  webhookName,
}: VerifyWebhookModalProps) {
  const [verified, setVerified] = useState(false);
  const [verifiedAt, setVerifiedAt] = useState('');
  const [checking, setChecking] = useState(false);
  const [notYet, setNotYet] = useState(false);
  // True when Visiopt already created the endpoint in the provider — the user must not add it manually
  const [autoCreated, setAutoCreated] = useState(false);

  // Instructions, display name and icon all come from the shared config
  const testStep = webhookTestStep(processor);
  const platformName = testStep?.label || processor;
  const platformLogo = testStep?.logo;
  const platformGuides = platformGuidesFor(processor);

  // Theme-aware palette so the modal reads correctly in light and dark
  const { colorScheme } = useMantineColorScheme();
  const dark = colorScheme === 'dark';
  const c = {
    heading: dark ? '#E5E7EB' : '#111827',
    body: dark ? '#9CA3AF' : '#4B5563',
    imgBorder: dark ? '#373A40' : '#EAEAEA',
    greenBg: dark ? 'rgba(6,95,70,0.18)' : '#ECFDF5',
    greenBorder: dark ? '#0F5132' : '#A7F3D0',
    greenTitle: dark ? '#6EE7B7' : '#065F46',
    greenText: dark ? '#A7F3D0' : '#047857',
    amberBg: dark ? 'rgba(146,64,14,0.18)' : '#FFFBEB',
    amberBorder: dark ? '#78350F' : '#FDE68A',
    amberTitle: dark ? '#FCD34D' : '#92400E',
    purpleBg: dark ? 'rgba(109,40,217,0.15)' : '#F5F3FF',
    purpleBorder: dark ? '#4C3F76' : '#DDD6FE',
    purpleLink: dark ? '#C4B5FD' : '#6D28D9',
  };

  const checkStatus = useCallback(
    (showMiss: boolean) => {
      if (!webhookId) return;
      setChecking(true);
      setNotYet(false);
      SettingsApi.getwebhookverificationstatus({ webhook_id: webhookId })
        .then((r: any) => (r.ok ? r.json() : null))
        .then((res: any) => {
          setChecking(false);
          if (!res || res.status !== 1 || !res.data) return;
          const isVerified = Number(res.data.verification_status) === 1;
          setVerified(isVerified);
          setVerifiedAt(res.data.verified_at || '');
          setAutoCreated(Number(res.data.auto_created) === 1);
          if (!isVerified && showMiss) setNotYet(true);
        })
        .catch(() => setChecking(false));
    },
    [webhookId]
  );

  // Read the current status whenever the modal opens
  useEffect(() => {
    if (!opened) return;
    setVerified(false);
    setVerifiedAt('');
    setNotYet(false);
    setAutoCreated(false);
    checkStatus(false);
  }, [opened, checkStatus]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      radius="lg"
      size={560}
      zIndex={10000}
      title={
        <Group spacing={10} noWrap>
          {platformLogo ? (
            <img src={platformLogo} alt={platformName} style={{ width: 28, height: 28, objectFit: 'contain' }} />
          ) : null}
          <Text fw={700} fz="md">
            Verify Your Webhook
          </Text>
        </Group>
      }
    >
      <Text fz={14} c="dimmed" lh={1.6}>
        Sending a test event confirms that Visiopt can successfully receive webhook data from {platformName}.
      </Text>

      {/* Auto-created: Visiopt already added the endpoint, so the user only confirms it — never re-adds it */}
      {webhookUrl && autoCreated && (
        <Box mt={16}>
          <Group position="apart" mb={6}>
            <Text fw={700} fz="sm" c={c.greenTitle}>
              1. Visiopt already created this webhook in {platformName}
            </Text>
            {webhookName && (
              <Text fz={12.5} c="dimmed" truncate>
                {webhookName}
              </Text>
            )}
          </Group>
          <Text fz={13} c={c.body} lh={1.6} mb={8}>
            No manual setup needed — Visiopt added the endpoint to your {platformName} account. This is the URL it
            created, for your reference.
          </Text>
          <Group spacing={8} noWrap>
            <TextInput style={{ flex: 1 }} size="sm" radius="md" readOnly value={webhookUrl} />
            <CopyButton value={webhookUrl} timeout={1500}>
              {({ copied, copy }) => (
                <Button
                  size="sm"
                  radius="md"
                  color={copied ? 'teal' : 'blue'}
                  leftIcon={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                  onClick={copy}
                >
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              )}
            </CopyButton>
          </Group>
        </Box>
      )}

      {/* Not auto-created: the endpoint does not exist in the platform yet — the user needs this URL to add it */}
      {webhookUrl && !autoCreated && (
        <Box mt={16}>
          <Group position="apart" mb={6}>
            <Text fw={700} fz="sm" c={c.heading}>
              1. Add this webhook URL in {platformName}
            </Text>
            {webhookName && (
              <Text fz={12.5} c="dimmed" truncate>
                {webhookName}
              </Text>
            )}
          </Group>
          <Group spacing={8} noWrap>
            <TextInput style={{ flex: 1 }} size="sm" radius="md" readOnly value={webhookUrl} />
            <CopyButton value={webhookUrl} timeout={1500}>
              {({ copied, copy }) => (
                <Button
                  size="sm"
                  radius="md"
                  color={copied ? 'teal' : 'blue'}
                  leftIcon={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                  onClick={copy}
                >
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              )}
            </CopyButton>
          </Group>
        </Box>
      )}

      {testStep && (
        <Box mt={16}>
          <Text fw={700} fz="sm" c={c.heading} mb={6}>
            {webhookUrl ? '2. Send a test event' : 'How to Send a Test Event'}
          </Text>
          <Text fz={13.5} c={c.body} lh={1.6}>
            {testStep.text}
          </Text>
          <Text fz={13.5} c={c.body} lh={1.6} mt={6}>
            Then return here and click <b>Refresh</b>.
          </Text>
          {testStep.image && (
            <img
              src={testStep.image}
              alt={`Send a test webhook from ${platformName}`}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
              style={{
                display: 'block',
                maxWidth: '100%',
                height: 'auto',
                borderRadius: 10,
                border: `1px solid ${c.imgBorder}`,
                marginTop: 10,
              }}
            />
          )}
        </Box>
      )}

      <Box mt={18}>
        <Text fw={700} fz="sm" c={c.heading} mb={8}>
          Verification Status
        </Text>

        {verified ? (
          <Box style={{ background: c.greenBg, border: `1px solid ${c.greenBorder}`, borderRadius: 10, padding: '12px 14px' }}>
            <Group spacing={8} noWrap>
              <IconCircleCheck size={18} color="#059669" />
              <Text fz={14} fw={700} c={c.greenTitle}>
                Verified
              </Text>
            </Group>
            <Text fz={13} c={c.greenText} mt={4}>
              Test webhook received successfully.
            </Text>
            {verifiedAt && (
              <Text fz={12.5} c={c.greenText} mt={6}>
                Last verified: {formatVerifiedAt(verifiedAt)}
              </Text>
            )}
          </Box>
        ) : (
          <Box style={{ background: c.amberBg, border: `1px solid ${c.amberBorder}`, borderRadius: 10, padding: '12px 14px' }}>
            <Group position="apart" noWrap>
              <Group spacing={8} noWrap>
                {checking ? <Loader size="xs" color="orange" /> : <IconClockHour4 size={18} color="#B45309" />}
                <Text fz={14} fw={700} c={c.amberTitle}>
                  {checking ? 'Checking…' : 'Waiting for test event'}
                </Text>
              </Group>
              <Tooltip label="Check for a test event" withArrow>
                <ActionIcon
                  variant="subtle"
                  color="orange"
                  size="lg"
                  title="Refresh verification status"
                  loading={checking}
                  onClick={() => checkStatus(true)}
                >
                  <IconRefresh size="1.1rem" />
                </ActionIcon>
              </Tooltip>
            </Group>
            {notYet && (
              <Text fz={13} c={c.amberTitle} mt={6}>
                No test event received yet. Send one from {platformName}, then click Refresh again.
              </Text>
            )}
          </Box>
        )}
      </Box>

      {/* Optional Advanced Tracking — shown for every provider */}
      <Box mt={18} style={{ background: c.purpleBg, border: `1px solid ${c.purpleBorder}`, borderRadius: 10, padding: '12px 14px' }}>
        <Text fw={700} fz="sm" c={c.heading} mb={6}>
          Optional Advanced Tracking
        </Text>
        <Text fz={13} c={c.body} lh={1.6}>
          Your main webhook is now configured and ready to track conversions.
        </Text>
        <Text fz={13} c={c.body} lh={1.6} mt={8}>
          If you need more advanced webhook tracking in the future, Visiopt also supports:
        </Text>
        <ul style={{ margin: '8px 0 0', paddingLeft: 18, color: c.body, fontSize: 13, lineHeight: 1.6 }}>
          <li>Track individual products or plans separately</li>
          <li>Track opt-ins, surveys, quizzes, and other events</li>
          <li>Merge multiple webhooks into one reporting goal</li>
          <li>Work with systems that only support a single webhook connection</li>
        </ul>
        <Anchor href={ADVANCED_WEBHOOK_DOC_URL} target="_blank" rel="noreferrer" fw={700} fz={13} c={c.purpleLink} mt={10} style={{ display: 'inline-block' }}>
          Learn about Advanced Webhook Settings
        </Anchor>
      </Box>

      {/* Platform-Specific Guides — only for providers with curated scenarios */}
      {platformGuides && (
        <Box mt={12} style={{ background: c.purpleBg, border: `1px solid ${c.purpleBorder}`, borderRadius: 10, padding: '12px 14px' }}>
          <Text fw={700} fz="sm" c={c.heading} mb={6}>
            Platform-Specific Guides
          </Text>
          <Text fz={13} c={c.body} lh={1.6}>
            Need help configuring additional webhooks for {platformGuides.label}?
          </Text>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
            {platformGuides.links.map((lnk) => (
              <Anchor key={lnk.url} href={lnk.url} target="_blank" rel="noreferrer" fw={700} fz={13} c={c.purpleLink}>
                {lnk.label}
              </Anchor>
            ))}
          </div>
        </Box>
      )}

      <Group position="apart" mt={22}>
        <Button
          variant="default"
          radius="md"
          leftIcon={<IconRefresh size={16} />}
          loading={checking}
          onClick={() => checkStatus(true)}
        >
          Refresh
        </Button>
        <Button color="dark" radius="md" onClick={onClose}>
          Close
        </Button>
      </Group>
    </Modal>
  );
}
